import { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Building, Users, FileText, DollarSign, TrendingUp,
  GitBranch, Search, MoreHorizontal, Shield, Power,
  Crown, ChevronDown, Activity, Plus, Mail,
  MousePointerClick, ArrowUpRight, BarChart3, Eye,
  Globe, Megaphone, UserCheck, Settings, Key, Send, Check, AlertTriangle,
  Trash2, LogIn, Clock
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel
} from "../components/ui/dropdown-menu";
import ReferralDashboard from "../components/ReferralDashboard";
import UTMAnalytics from "../components/UTMAnalytics";

const PLAN_COLORS = { starter: "#64748b", pro: "#1a56db", team: "#7C3AED", enterprise: "#D97706" };

const PlatformAdmin = () => {
  const { user, startImpersonation } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [starterTemplates, setStarterTemplates] = useState([]);
  const [search, setSearch] = useState("");
  const [planOverride, setPlanOverride] = useState({ org_id: null, current: "" });
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newTenant, setNewTenant] = useState({ org_name: "", owner_name: "", owner_email: "", plan_id: "starter" });
  const [creating, setCreating] = useState(false);
  const [referralData, setReferralData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [systemConfig, setSystemConfig] = useState({});
  const [configInputs, setConfigInputs] = useState({ RESEND_API_KEY: "", STRIPE_API_KEY: "", SYSTEM_FROM_EMAIL: "", SYSTEM_SENDER_NAME: "" });
  const [savingConfig, setSavingConfig] = useState(null);
  const [testingEmail, setTestingEmail] = useState(false);
  const [purgeEmail, setPurgeEmail] = useState("");
  const [purging, setPurging] = useState(false);
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const [deleteTenant, setDeleteTenant] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [loggingIn, setLoggingIn] = useState(null);

  useEffect(() => {
    if (user?.is_platform_admin) {
      fetchAll();
    }
  }, [user]);

  const fetchAll = () => {
    axios.get(`${API}/platform/tenants`, { withCredentials: true }).then(r => setTenants(r.data)).catch(() => {});
    axios.get(`${API}/platform/metrics`, { withCredentials: true }).then(r => setMetrics(r.data)).catch(() => {});
    axios.get(`${API}/platform/starter-templates`, { withCredentials: true }).then(r => setStarterTemplates(r.data)).catch(() => {});
    axios.get(`${API}/platform/referrals`, { withCredentials: true }).then(r => setReferralData(r.data)).catch(() => {});
    axios.get(`${API}/platform/analytics`, { withCredentials: true }).then(r => setAnalyticsData(r.data)).catch(() => {});
    axios.get(`${API}/platform/system-config`, { withCredentials: true }).then(r => setSystemConfig(r.data)).catch(() => {});
  };

  const handleSaveConfig = async (key) => {
    const value = configInputs[key];
    if (!value?.trim()) return;
    setSavingConfig(key);
    try {
      await axios.put(`${API}/platform/system-config`, { key, value: value.trim() }, { withCredentials: true });
      toast.success(`${key} updated successfully`);
      setConfigInputs(p => ({ ...p, [key]: "" }));
      // Refresh config
      axios.get(`${API}/platform/system-config`, { withCredentials: true }).then(r => setSystemConfig(r.data)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to update ${key}`);
    } finally {
      setSavingConfig(null);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      const res = await axios.post(`${API}/platform/system-config/test-email`, {}, { withCredentials: true });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test email failed");
    } finally {
      setTestingEmail(false);
    }
  };

  const toggleTenantStatus = async (orgId, currentActive) => {
    try {
      await axios.put(`${API}/platform/tenants/${orgId}/status`, { is_active: !currentActive }, { withCredentials: true });
      toast.success(currentActive ? "Tenant deactivated" : "Tenant activated");
      fetchAll();
    } catch { toast.error("Failed to update status"); }
  };

  const handlePlanOverride = async () => {
    try {
      await axios.put(`${API}/platform/tenants/${planOverride.org_id}/subscription`, { plan_id: planOverride.current }, { withCredentials: true });
      toast.success("Plan overridden");
      setShowPlanDialog(false);
      fetchAll();
    } catch { toast.error("Failed to override plan"); }
  };

  const handleCreateTenant = async () => {
    if (!newTenant.org_name || !newTenant.owner_name || !newTenant.owner_email) {
      toast.error("All fields are required");
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/platform/tenants`, newTenant, { withCredentials: true });
      toast.success(`Tenant "${res.data.name}" created (${res.data.cloned_templates} templates cloned)`);
      setShowCreateDialog(false);
      setNewTenant({ org_name: "", owner_name: "", owner_email: "", plan_id: "starter" });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create tenant");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteTenant = async () => {
    if (!deleteTenant) return;
    setDeleting(true);
    try {
      const res = await axios.delete(`${API}/platform/tenants/${deleteTenant.org_id}`, { withCredentials: true });
      const d = res.data.deleted || {};
      const summary = Object.entries(d).map(([k, v]) => `${k}: ${v}`).join(", ");
      toast.success(`Deleted ${deleteTenant.name}: ${summary}`);
      setDeleteTenant(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete tenant");
    } finally {
      setDeleting(false);
    }
  };

  const handleLoginAs = async (tenant) => {
    setLoggingIn(tenant.org_id);
    try {
      const res = await axios.post(`${API}/platform/tenants/${tenant.org_id}/login-as`, {}, { withCredentials: true });
      toast.success(res.data.message);
      // Refresh auth state to pick up impersonation
      window.location.href = "/";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to login as tenant");
    } finally {
      setLoggingIn(null);
    }
  };

  const formatLastActive = (ts) => {
    if (!ts) return "Never";
    try {
      const d = new Date(ts);
      if (isNaN(d.getTime())) return "Unknown";
      const now = new Date();
      const diffMs = now - d;
      const diffH = diffMs / (1000 * 60 * 60);
      if (diffH < 1) return "Just now";
      if (diffH < 24) return `${Math.floor(diffH)}h ago`;
      const diffD = diffH / 24;
      if (diffD < 7) return `${Math.floor(diffD)}d ago`;
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch { return "Unknown"; }
  };

  if (!user?.is_platform_admin) {
    return (
      <div className="p-8 text-center text-[var(--t-text-muted)]">
        <Shield className="w-12 h-12 mx-auto mb-4 opacity-30" />
        <p>Platform admin access required.</p>
      </div>
    );
  }

  const filtered = tenants.filter(t =>
    t.name?.toLowerCase().includes(search.toLowerCase()) ||
    t.owner_email?.toLowerCase().includes(search.toLowerCase()) ||
    t.owner_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="platform-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--t-text)] [font-family:var(--t-heading-font)]">
            Platform Administration
          </h1>
          <p className="text-sm text-[var(--t-text-muted)] mt-1">Manage tenants, monitor usage, and control subscriptions</p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={() => setShowCreateDialog(true)} className="btn-primary gap-2" data-testid="create-tenant-btn">
            <Plus className="w-4 h-4" /> New Tenant
          </Button>
          <Badge className="bg-amber-100 text-amber-700 border-amber-200 gap-1 text-xs">
            <Crown className="w-3 h-3" /> Super Admin
          </Badge>
        </div>
      </div>

      {/* Metrics Grid */}
      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="platform-metrics">
          {[
            { label: "Tenants", value: metrics.total_orgs, icon: Building, color: "#1a56db" },
            { label: "Users", value: metrics.total_users, icon: Users, color: "#7C3AED" },
            { label: "Transactions", value: metrics.total_transactions, icon: FileText, color: "#059669" },
            { label: "Active Txns", value: metrics.active_transactions, icon: Activity, color: "#D97706" },
            { label: "Workflows", value: metrics.total_workflows, icon: GitBranch, color: "#DC2626" },
            { label: "MRR", value: `$${metrics.mrr.toLocaleString()}`, icon: DollarSign, color: "#059669" },
            { label: "New (30d)", value: metrics.recent_signups, icon: TrendingUp, color: "#1a56db" },
          ].map((m) => (
            <Card key={m.label} className="card-surface">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: m.color + "15" }}>
                    <m.icon className="w-3.5 h-3.5" style={{ color: m.color }} />
                  </div>
                </div>
                <p className="text-xl font-bold text-[var(--t-text)]">{m.value}</p>
                <p className="text-[11px] text-[var(--t-text-muted)]">{m.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Starter Templates */}
      <Card className="card-surface">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <GitBranch className="w-4 h-4 text-[var(--t-primary)]" />
            Starter Templates
          </CardTitle>
          <CardDescription>These workflows are cloned to every new tenant on signup</CardDescription>
        </CardHeader>
        <CardContent>
          {starterTemplates.length === 0 ? (
            <p className="text-sm text-[var(--t-text-muted)]">No starter templates. Your workflows will be used as the source.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {starterTemplates.map(t => (
                <Badge key={t.template_id} variant="outline" className="text-xs py-1.5 px-3 gap-1.5">
                  <GitBranch className="w-3 h-3" />
                  {t.name}
                  <span className="text-[var(--t-text-muted)]">({t.task_count} tasks)</span>
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tenant List */}
      <Card className="card-surface" data-testid="tenant-list">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Building className="w-4 h-4 text-[var(--t-primary)]" />
                Tenants
              </CardTitle>
              <CardDescription>{tenants.length} organization{tenants.length !== 1 ? "s" : ""} registered</CardDescription>
              <p className="text-xs text-[var(--t-text-muted)] mt-1 flex items-center gap-1.5">
                <Mail className="w-3 h-3" />
                Help & Support: <a href="mailto:service@easyoffice.systems" className="text-[var(--t-primary)] hover:underline font-medium" data-testid="support-email-link">service@easyoffice.systems</a>
              </p>
            </div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--t-text-muted)]" />
              <Input
                placeholder="Search tenants..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-8 w-64 text-sm"
                data-testid="tenant-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--t-border)] text-left text-[var(--t-text-muted)]">
                  <th className="px-5 py-3 font-medium">Organization</th>
                  <th className="px-5 py-3 font-medium">Owner</th>
                  <th className="px-5 py-3 font-medium">Plan</th>
                  <th className="px-5 py-3 font-medium text-center">Users</th>
                  <th className="px-5 py-3 font-medium text-center">Txns</th>
                  <th className="px-5 py-3 font-medium">Last Active</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.org_id} className="border-b border-[var(--t-border)] hover:bg-[var(--t-bg)] transition-colors" data-testid={`tenant-row-${t.org_id}`}>
                    <td className="px-5 py-3">
                      <div className="font-medium text-[var(--t-text)]">{t.name}</div>
                      <div className="text-[11px] text-[var(--t-text-muted)]">{t.org_id}</div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        {t.owner_picture && <img src={t.owner_picture} alt="" className="w-6 h-6 rounded-full" />}
                        <div>
                          <div className="text-[var(--t-text)]">{t.owner_name}</div>
                          <div className="text-[11px] text-[var(--t-text-muted)]">{t.owner_email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <Badge
                        className="text-[10px]"
                        style={{ background: (PLAN_COLORS[t.plan_id] || "#64748b") + "15", color: PLAN_COLORS[t.plan_id] || "#64748b", border: `1px solid ${(PLAN_COLORS[t.plan_id] || "#64748b")}30` }}
                      >
                        {t.plan}
                      </Badge>
                    </td>
                    <td className="px-5 py-3 text-center text-[var(--t-text)]">{t.user_count}</td>
                    <td className="px-5 py-3 text-center text-[var(--t-text)]">
                      {t.active_transaction_count}<span className="text-[var(--t-text-muted)]">/{t.transaction_count}</span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-xs text-[var(--t-text-muted)] flex items-center gap-1" data-testid={`tenant-last-active-${t.org_id}`}>
                        <Clock className="w-3 h-3" /> {formatLastActive(t.last_active)}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <Badge variant={t.is_active ? "default" : "destructive"} className={`text-[10px] ${t.is_active ? "bg-green-100 text-green-700 border-green-200" : ""}`}>
                        {t.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`tenant-actions-${t.org_id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel className="text-xs">{t.name}</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleLoginAs(t)}
                            disabled={loggingIn === t.org_id}
                            data-testid={`login-as-${t.org_id}`}
                          >
                            <LogIn className="w-3.5 h-3.5 mr-2" />
                            {loggingIn === t.org_id ? "Logging in..." : "Login As"}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setPlanOverride({ org_id: t.org_id, current: t.plan_id }); setShowPlanDialog(true); }}>
                            <Crown className="w-3.5 h-3.5 mr-2" /> Change Plan
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toggleTenantStatus(t.org_id, t.is_active)}>
                            <Power className="w-3.5 h-3.5 mr-2" />
                            {t.is_active ? "Deactivate" : "Activate"}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setDeleteTenant(t)}
                            className="text-red-600 focus:text-red-600"
                            data-testid={`delete-tenant-${t.org_id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete Tenant
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-5 py-8 text-center text-[var(--t-text-muted)]">
                      {search ? "No tenants match your search" : "No tenants registered yet"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <ReferralDashboard referralData={referralData} />

      {/* ─── Landing Page Analytics ─── */}
      <Card className="card-surface" data-testid="analytics-dashboard">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[var(--t-primary)]" />
            Landing Page Analytics
          </CardTitle>
          <CardDescription>Track traffic sources from Facebook, Instagram, TikTok, and other campaigns</CardDescription>
        </CardHeader>
        <CardContent>
          {analyticsData ? (
            <div className="space-y-6">
              {/* Summary Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="analytics-summary">
                {[
                  { label: "Total Visits", value: analyticsData.total_visits, icon: Eye, color: "#1a56db" },
                  { label: "Signups (30d)", value: analyticsData.recent_signups, icon: UserCheck, color: "#059669" },
                  { label: "Sources", value: analyticsData.top_sources?.length || 0, icon: Globe, color: "#7C3AED" },
                  { label: "Campaigns", value: analyticsData.top_campaigns?.length || 0, icon: Megaphone, color: "#D97706" },
                ].map((m) => (
                  <div key={m.label} className="bg-[var(--t-bg)] rounded-lg p-4 border border-[var(--t-border)]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-wider text-[var(--t-text-muted)] font-medium">{m.label}</span>
                      <m.icon className="w-4 h-4" style={{ color: m.color }} />
                    </div>
                    <p className="text-2xl font-bold text-[var(--t-text)]" style={{ fontFamily: "var(--t-heading-font)" }}>{m.value}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Traffic by Source */}
                <div className="border border-[var(--t-border)] rounded-lg overflow-hidden" data-testid="analytics-by-source">
                  <div className="px-4 py-2.5 bg-[var(--t-bg)] border-b border-[var(--t-border)]">
                    <p className="text-xs font-semibold text-[var(--t-text)] flex items-center gap-1.5">
                      <Globe className="w-3 h-3 text-blue-500" /> Traffic by Source
                    </p>
                  </div>
                  {analyticsData.top_sources?.length === 0 ? (
                    <div className="p-6 text-center text-sm text-[var(--t-text-muted)]">
                      No visits tracked yet. Share your landing page with UTM parameters to start tracking.
                      <div className="mt-3 bg-[var(--t-bg)] rounded-md p-3 text-xs text-left font-mono break-all">
                        yoursite.com<span className="text-[var(--t-primary)]">?utm_source=facebook&utm_campaign=launch</span>
                      </div>
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-[var(--t-border)] bg-[var(--t-bg)]/50">
                          <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-[var(--t-text-muted)] font-medium">Source</th>
                          <th className="text-center px-4 py-2 text-[10px] uppercase tracking-wider text-[var(--t-text-muted)] font-medium">Visits</th>
                          <th className="text-center px-4 py-2 text-[10px] uppercase tracking-wider text-[var(--t-text-muted)] font-medium">Signups</th>
                          <th className="text-center px-4 py-2 text-[10px] uppercase tracking-wider text-[var(--t-text-muted)] font-medium">Conv. Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analyticsData.top_sources.map((s) => (
                          <tr key={s.source} className="border-b border-[var(--t-border)] last:border-0 hover:bg-[var(--t-bg)]">
                            <td className="px-4 py-2.5">
                              <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${
                                  s.source === "facebook" ? "bg-blue-500" :
                                  s.source === "instagram" ? "bg-pink-500" :
                                  s.source === "tiktok" ? "bg-gray-800" :
                                  s.source === "google" ? "bg-red-500" :
                                  s.source === "direct" ? "bg-gray-400" :
                                  "bg-[var(--t-primary)]"
                                }`} />
                                <span className="font-medium text-[var(--t-text)] text-xs capitalize">{s.source}</span>
                              </div>
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <Badge variant="outline" className="text-[10px]">{s.visits}</Badge>
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <Badge className={`text-[10px] ${s.signups > 0 ? "bg-green-100 text-green-700 border-green-200" : "bg-gray-100 text-gray-500 border-gray-200"}`}>
                                {s.signups}
                              </Badge>
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <span className={`text-xs font-medium ${s.rate > 0 ? "text-green-600" : "text-[var(--t-text-muted)]"}`}>
                                {s.rate}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Campaigns + Daily Chart */}
                <div className="space-y-4">
                  {/* Top Campaigns */}
                  <div className="border border-[var(--t-border)] rounded-lg overflow-hidden" data-testid="analytics-campaigns">
                    <div className="px-4 py-2.5 bg-[var(--t-bg)] border-b border-[var(--t-border)]">
                      <p className="text-xs font-semibold text-[var(--t-text)] flex items-center gap-1.5">
                        <Megaphone className="w-3 h-3 text-amber-500" /> Top Campaigns
                      </p>
                    </div>
                    {analyticsData.top_campaigns?.length === 0 ? (
                      <div className="p-6 text-center text-sm text-[var(--t-text-muted)]">No campaigns tracked yet</div>
                    ) : (
                      <div className="divide-y divide-[var(--t-border)]">
                        {analyticsData.top_campaigns.map((c) => (
                          <div key={c.campaign} className="flex items-center justify-between px-4 py-2.5 hover:bg-[var(--t-bg)]">
                            <span className="text-xs font-medium text-[var(--t-text)]">{c.campaign}</span>
                            <Badge variant="outline" className="text-[10px]">{c.visits} visits</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Daily Traffic Mini-Chart */}
                  {analyticsData.daily_data?.length > 0 && (
                    <div className="border border-[var(--t-border)] rounded-lg overflow-hidden" data-testid="analytics-daily">
                      <div className="px-4 py-2.5 bg-[var(--t-bg)] border-b border-[var(--t-border)]">
                        <p className="text-xs font-semibold text-[var(--t-text)] flex items-center gap-1.5">
                          <TrendingUp className="w-3 h-3 text-green-500" /> Daily Visits (Last 30 Days)
                        </p>
                      </div>
                      <div className="p-4">
                        <div className="flex items-end gap-[3px] h-[80px]">
                          {analyticsData.daily_data.map((d) => {
                            const maxVisits = Math.max(...analyticsData.daily_data.map(x => x.visits));
                            const height = maxVisits > 0 ? Math.max((d.visits / maxVisits) * 100, 4) : 4;
                            return (
                              <div
                                key={d.date}
                                className="flex-1 rounded-t-sm bg-[var(--t-primary)] opacity-70 hover:opacity-100 transition-opacity cursor-default"
                                style={{ height: `${height}%` }}
                                title={`${d.date}: ${d.visits} visit${d.visits !== 1 ? 's' : ''}`}
                              />
                            );
                          })}
                        </div>
                        <div className="flex justify-between mt-1.5">
                          <span className="text-[9px] text-[var(--t-text-muted)]">{analyticsData.daily_data[0]?.date?.slice(5)}</span>
                          <span className="text-[9px] text-[var(--t-text-muted)]">{analyticsData.daily_data[analyticsData.daily_data.length - 1]?.date?.slice(5)}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Recent Visits */}
                  <div className="border border-[var(--t-border)] rounded-lg overflow-hidden" data-testid="analytics-recent">
                    <div className="px-4 py-2.5 bg-[var(--t-bg)] border-b border-[var(--t-border)]">
                      <p className="text-xs font-semibold text-[var(--t-text)] flex items-center gap-1.5">
                        <MousePointerClick className="w-3 h-3 text-purple-500" /> Recent Visits
                      </p>
                    </div>
                    {analyticsData.recent_visits?.length === 0 ? (
                      <div className="p-6 text-center text-sm text-[var(--t-text-muted)]">No visits yet</div>
                    ) : (
                      <div className="max-h-[200px] overflow-y-auto divide-y divide-[var(--t-border)]">
                        {analyticsData.recent_visits?.map((v, i) => (
                          <div key={i} className="flex items-center justify-between px-4 py-2 hover:bg-[var(--t-bg)]">
                            <div className="min-w-0">
                              <div className="text-xs text-[var(--t-text)]">
                                <span className="font-medium capitalize">{v.utm_source || "direct"}</span>
                                {v.utm_campaign && <span className="text-[var(--t-text-muted)]"> / {v.utm_campaign}</span>}
                              </div>
                              <div className="text-[10px] text-[var(--t-text-muted)]">
                                {v.utm_medium && <span>{v.utm_medium} &middot; </span>}
                                {new Date(v.timestamp).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                              </div>
                            </div>
                            <ArrowUpRight className="w-3 h-3 text-[var(--t-text-muted)] shrink-0" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* UTM Cheatsheet */}
              <div className="bg-[var(--t-bg)] rounded-lg border border-[var(--t-border)] p-4" data-testid="utm-cheatsheet">
                <p className="text-xs font-semibold text-[var(--t-text)] mb-2">UTM Link Builder</p>
                <p className="text-[11px] text-[var(--t-text-muted)] mb-3">Copy these links for your ad campaigns:</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {[
                    { platform: "Facebook", src: "facebook", color: "bg-blue-500" },
                    { platform: "Instagram", src: "instagram", color: "bg-pink-500" },
                    { platform: "TikTok", src: "tiktok", color: "bg-gray-800" },
                  ].map((p) => {
                    const url = `${window.location.origin}?utm_source=${p.src}&utm_medium=social&utm_campaign=launch`;
                    return (
                      <div key={p.src} className="flex items-center gap-2 bg-white rounded-md border border-[var(--t-border)] p-2.5">
                        <span className={`w-2.5 h-2.5 rounded-full ${p.color} shrink-0`} />
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] font-semibold text-[var(--t-text)]">{p.platform}</p>
                          <p className="text-[9px] text-[var(--t-text-muted)] truncate font-mono">{url}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-[10px] shrink-0"
                          onClick={() => { navigator.clipboard.writeText(url); toast.success(`${p.platform} link copied!`); }}
                          data-testid={`copy-utm-${p.src}`}
                        >
                          Copy
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-sm text-[var(--t-text-muted)]">Loading analytics data...</div>
          )}
        </CardContent>
      </Card>

      {/* Purge User Tool */}
      <Card className="card-surface border-2 border-red-200" data-testid="purge-user-card">
        <CardHeader className="pb-3" style={{ borderBottom: '1px solid var(--t-border)' }}>
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            Purge User Data
          </CardTitle>
          <CardDescription>Permanently delete all data for a user email — user record, sessions, invites, and orphaned org data.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <Label className="text-xs uppercase tracking-wider text-[var(--t-text-muted)] mb-1 block">Email Address</Label>
              <Input
                type="email"
                placeholder="user@example.com"
                value={purgeEmail}
                onChange={(e) => setPurgeEmail(e.target.value)}
                data-testid="purge-email-input"
              />
            </div>
            <Button
              variant="destructive"
              disabled={!purgeEmail.trim() || purging}
              onClick={() => setShowPurgeConfirm(true)}
              data-testid="purge-user-btn"
            >
              {purging ? "Purging..." : "Purge User"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Purge Confirmation Dialog */}
      <Dialog open={showPurgeConfirm} onOpenChange={setShowPurgeConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Confirm Permanent Deletion
            </DialogTitle>
            <DialogDescription>
              This will permanently delete ALL data for <strong>{purgeEmail}</strong> — their user account, sessions, invites, and if their org is left empty, all transactions, workflows, contacts, and documents in that org. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPurgeConfirm(false)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={purging}
              onClick={async () => {
                setPurging(true);
                try {
                  const res = await axios.delete(`${API}/platform/purge-user/${encodeURIComponent(purgeEmail.trim())}`, { withCredentials: true });
                  const d = res.data.deleted || {};
                  const summary = Object.entries(d).map(([k, v]) => `${k}: ${v}`).join(", ");
                  toast.success(`Purged ${purgeEmail}: ${summary}`);
                  setPurgeEmail("");
                  fetchAll();
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Purge failed");
                } finally {
                  setPurging(false);
                  setShowPurgeConfirm(false);
                }
              }}
              data-testid="confirm-purge-btn"
            >
              {purging ? "Purging..." : "Yes, Permanently Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* System Configuration — API Keys stored in DB */}
      <Card className="card-surface border-2 border-amber-200" data-testid="system-config-card">
        <CardHeader className="pb-3" style={{ borderBottom: '1px solid var(--t-border)' }}>
          <CardTitle className="text-base flex items-center gap-2">
            <Settings className="w-4 h-4 text-amber-600" />
            System Configuration
          </CardTitle>
          <CardDescription>API keys stored in the database — survives deployments. No .env required.</CardDescription>
        </CardHeader>
        <CardContent className="p-5 space-y-5">
          {/* Resend API Key */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold flex items-center gap-2 text-[var(--t-text)]">
                <Key className="w-4 h-4 text-blue-600" /> Resend API Key
              </Label>
              {systemConfig.RESEND_API_KEY?.is_set ? (
                <Badge className="bg-green-50 text-green-700 border-green-200 text-xs gap-1"><Check className="w-3 h-3" /> Configured</Badge>
              ) : (
                <Badge className="bg-red-50 text-red-700 border-red-200 text-xs gap-1"><AlertTriangle className="w-3 h-3" /> Not Set</Badge>
              )}
            </div>
            {systemConfig.RESEND_API_KEY?.is_set && (
              <p className="text-xs text-[var(--t-text-muted)] font-mono">{systemConfig.RESEND_API_KEY.value_masked}</p>
            )}
            <div className="flex gap-2">
              <Input
                type="password"
                placeholder="re_xxxxxxxxx..."
                value={configInputs.RESEND_API_KEY}
                onChange={(e) => setConfigInputs(p => ({ ...p, RESEND_API_KEY: e.target.value }))}
                className="flex-1 font-mono text-sm"
                data-testid="input-resend-key"
              />
              <Button
                onClick={() => handleSaveConfig("RESEND_API_KEY")}
                disabled={!configInputs.RESEND_API_KEY?.trim() || savingConfig === "RESEND_API_KEY"}
                className="btn-primary"
                data-testid="save-resend-key"
              >
                {savingConfig === "RESEND_API_KEY" ? "Saving..." : "Save"}
              </Button>
              {systemConfig.RESEND_API_KEY?.is_set && (
                <Button variant="outline" onClick={handleTestEmail} disabled={testingEmail} data-testid="test-email-btn">
                  <Send className="w-4 h-4 mr-1" />
                  {testingEmail ? "Sending..." : "Test"}
                </Button>
              )}
            </div>
            <p className="text-xs text-[var(--t-text-subtle)]">
              Get your key at <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline text-blue-600">resend.com/api-keys</a>. Used for system emails (invites, digests).
            </p>
          </div>

          <Separator />

          {/* From Email */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold flex items-center gap-2 text-[var(--t-text)]">
              <Mail className="w-4 h-4 text-purple-600" /> System From Email
            </Label>
            {systemConfig.SYSTEM_FROM_EMAIL?.is_set && (
              <p className="text-xs text-[var(--t-text-muted)]">Current: {systemConfig.SYSTEM_FROM_EMAIL.value}</p>
            )}
            <div className="flex gap-2">
              <Input
                placeholder="noreply@easyoffice.systems"
                value={configInputs.SYSTEM_FROM_EMAIL}
                onChange={(e) => setConfigInputs(p => ({ ...p, SYSTEM_FROM_EMAIL: e.target.value }))}
                className="flex-1 text-sm"
                data-testid="input-from-email"
              />
              <Button
                onClick={() => handleSaveConfig("SYSTEM_FROM_EMAIL")}
                disabled={!configInputs.SYSTEM_FROM_EMAIL?.trim() || savingConfig === "SYSTEM_FROM_EMAIL"}
                className="btn-primary"
              >
                {savingConfig === "SYSTEM_FROM_EMAIL" ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>

          <Separator />

          {/* Sender Name */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold flex items-center gap-2 text-[var(--t-text)]">
              <UserCheck className="w-4 h-4 text-teal-600" /> System Sender Name
            </Label>
            {systemConfig.SYSTEM_SENDER_NAME?.is_set && (
              <p className="text-xs text-[var(--t-text-muted)]">Current: {systemConfig.SYSTEM_SENDER_NAME.value}</p>
            )}
            <div className="flex gap-2">
              <Input
                placeholder="EasyOffice Transaction Manager"
                value={configInputs.SYSTEM_SENDER_NAME}
                onChange={(e) => setConfigInputs(p => ({ ...p, SYSTEM_SENDER_NAME: e.target.value }))}
                className="flex-1 text-sm"
                data-testid="input-sender-name"
              />
              <Button
                onClick={() => handleSaveConfig("SYSTEM_SENDER_NAME")}
                disabled={!configInputs.SYSTEM_SENDER_NAME?.trim() || savingConfig === "SYSTEM_SENDER_NAME"}
                className="btn-primary"
              >
                {savingConfig === "SYSTEM_SENDER_NAME" ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Tenant Confirmation Dialog */}
      <Dialog open={!!deleteTenant} onOpenChange={(open) => !open && setDeleteTenant(null)}>
        <DialogContent data-testid="delete-tenant-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" /> Delete Tenant Permanently
            </DialogTitle>
            <DialogDescription>
              This will permanently delete <strong>{deleteTenant?.name}</strong> and ALL associated data — users, transactions, workflows, contacts, documents, and subscriptions. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            <p className="font-medium mb-1">Data to be deleted:</p>
            <ul className="text-xs space-y-0.5 ml-4 list-disc">
              <li>All users in this organization</li>
              <li>All transactions and activity logs</li>
              <li>All workflows and templates</li>
              <li>All contacts and documents</li>
              <li>Subscription and billing records</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTenant(null)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={deleting}
              onClick={handleDeleteTenant}
              data-testid="confirm-delete-tenant"
            >
              {deleting ? "Deleting..." : "Yes, Delete Everything"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plan Override Dialog */}
      <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
        <DialogContent data-testid="plan-override-dialog">
          <DialogHeader>
            <DialogTitle>Override Subscription Plan</DialogTitle>
            <DialogDescription>Change this tenant's plan. This bypasses billing.</DialogDescription>
          </DialogHeader>
          <Select value={planOverride.current} onValueChange={(v) => setPlanOverride(p => ({ ...p, current: v }))}>
            <SelectTrigger data-testid="plan-override-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="starter">Starter (Free)</SelectItem>
              <SelectItem value="pro">Professional ($99/mo)</SelectItem>
              <SelectItem value="team">Team ($249/mo)</SelectItem>
              <SelectItem value="enterprise">Enterprise (Custom)</SelectItem>
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPlanDialog(false)}>Cancel</Button>
            <Button onClick={handlePlanOverride} className="btn-primary" data-testid="confirm-plan-override">Apply Override</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Tenant Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent data-testid="create-tenant-dialog">
          <DialogHeader>
            <DialogTitle>Create New Tenant</DialogTitle>
            <DialogDescription>Set up a new organization with an owner account. Starter templates will be automatically cloned.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-[var(--t-text)] mb-1.5 block">Organization Name</label>
              <Input
                placeholder="e.g. Acme Realty Group"
                value={newTenant.org_name}
                onChange={(e) => setNewTenant(p => ({ ...p, org_name: e.target.value }))}
                data-testid="new-tenant-org-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[var(--t-text)] mb-1.5 block">Owner Name</label>
              <Input
                placeholder="e.g. Jane Smith"
                value={newTenant.owner_name}
                onChange={(e) => setNewTenant(p => ({ ...p, owner_name: e.target.value }))}
                data-testid="new-tenant-owner-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[var(--t-text)] mb-1.5 block">Owner Email</label>
              <Input
                type="email"
                placeholder="e.g. jane@acmerealty.com"
                value={newTenant.owner_email}
                onChange={(e) => setNewTenant(p => ({ ...p, owner_email: e.target.value }))}
                data-testid="new-tenant-owner-email"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[var(--t-text)] mb-1.5 block">Subscription Plan</label>
              <Select value={newTenant.plan_id} onValueChange={(v) => setNewTenant(p => ({ ...p, plan_id: v }))}>
                <SelectTrigger data-testid="new-tenant-plan">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter (Free)</SelectItem>
                  <SelectItem value="pro">Professional ($99/mo)</SelectItem>
                  <SelectItem value="team">Team ($249/mo)</SelectItem>
                  <SelectItem value="enterprise">Enterprise (Custom)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTenant} disabled={creating} className="btn-primary" data-testid="confirm-create-tenant">
              {creating ? "Creating..." : "Create Tenant"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformAdmin;
